import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  supportedLanguages,
  systemRoleKeySchema,
  type Language,
  type LoginDefault,
  type OrgType,
  type OrgTypeProfile,
  type OrgTypeProfileList,
  type SystemRoleKey,
  type UpdateOrgTypeProfileRequest,
} from '@up2d8/contracts';
import { LoadingState } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import {
  AdminPage,
  Badge,
  Btn,
  Card,
  CardHead,
  CardPad,
  Field,
  Input,
  Note,
  PageHead,
  Select,
  SwitchRow,
  TabPanel,
  Table,
  Tabs,
  useAdminToast,
  type TabItem,
} from '../admin-kit';

const ROLE_KEYS: readonly SystemRoleKey[] = systemRoleKeySchema.options;
type RoleNameForm = Record<SystemRoleKey, Partial<Record<Language, string>>>;

/**
 * Organisatietypes (ORG-3, ADR-0099): per type het startprofiel dat een organisatie krijgt bij het
 * aanmaken of bij een ander type.
 *
 * Drie delen: welke functies aan staan, hoe nieuwe mensen zich aanmelden, en hoe de systeemrollen
 * heten. Wat gelijk is aan de code, bewaart het scherm niet als eigen keuze: zo volgt het een latere
 * wijziging in de code, en is "Terug naar standaard" niets meer dan alles leeg laten. Bestaande
 * organisaties veranderen niet mee; het scherm zegt dat erbij.
 */
export function OrgTypesPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useAdminToast();
  const [tab, setTab] = useState<OrgType>('sports_club');

  const profiles = useQuery({
    queryKey: ['platform', 'org-types'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/platform/org-types');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as OrgTypeProfileList;
    },
  });

  const tabs: TabItem<OrgType>[] = (profiles.data?.items ?? []).map((profile) => ({
    key: profile.orgType,
    label: t(`beheer.orgType.types.${profile.orgType}`),
    count: profile.tenantCount,
    testId: `org-type-tab-${profile.orgType}`,
  }));
  const current = profiles.data?.items.find((profile) => profile.orgType === tab);

  return (
    <AdminPage>
      <PageHead title={t('beheer.orgType.page.title')} sub={t('beheer.orgType.page.sub')} />
      {profiles.isPending ? (
        <LoadingState />
      ) : profiles.isError || !profiles.data ? (
        <Note tone="danger" role="alert">
          {t('errors.generic')}
        </Note>
      ) : (
        <>
          <Tabs
            label={t('beheer.orgType.page.title')}
            idPrefix="org-types"
            tabs={tabs}
            value={tab}
            onChange={setTab}
          />
          <TabPanel idPrefix="org-types" tabKey={tab} className="a-stack">
            {current ? (
              // Een nieuwe sleutel na elke opslag: het formulier begint opnieuw bij wat de server zegt.
              <ProfileForm
                key={`${current.orgType}:${current.updatedAt ?? 'code'}`}
                profile={current}
                profileFlags={profiles.data.profileFlags}
                onSaved={toast.show}
              />
            ) : null}
          </TabPanel>
        </>
      )}
      {toast.element}
    </AdminPage>
  );
}

function ProfileForm({
  profile,
  profileFlags,
  onSaved,
}: {
  profile: OrgTypeProfile;
  profileFlags: string[];
  onSaved: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const code = profile.codeDefault;

  const [flags, setFlags] = useState<Record<string, boolean>>(profile.flags);
  const [loginDefault, setLoginDefault] = useState<LoginDefault>(profile.loginDefault);
  // Alleen wat afwijkt van de code staat in een veld; de naam uit de code is de plaatshouder.
  const [roleNames, setRoleNames] = useState<RoleNameForm>(
    () =>
      Object.fromEntries(
        ROLE_KEYS.map((key) => [
          key,
          Object.fromEntries(
            supportedLanguages.map((language) => {
              const value = profile.roleNames[key]?.[language] ?? '';
              return [language, value === code.roleNames[key]?.[language] ? '' : value];
            }),
          ),
        ]),
      ) as RoleNameForm,
  );

  const save = useMutation({
    mutationFn: async (body: UpdateOrgTypeProfileRequest) => {
      const { error } = await api.PUT('/api/v1/platform/org-types/{orgType}', {
        params: { path: { orgType: profile.orgType } },
        body: body as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      onSaved(t('beheer.orgType.page.saved'));
      await queryClient.invalidateQueries({ queryKey: ['platform'] });
    },
  });

  /** Alleen wat afwijkt van de code; is er niets, dan `null` en geldt de code weer. */
  const request = (): UpdateOrgTypeProfileRequest => {
    const flagsDiffer = profileFlags.some((key) => flags[key] !== code.flags[key]);
    const names = Object.fromEntries(
      ROLE_KEYS.map((key) => [
        key,
        Object.fromEntries(
          Object.entries(roleNames[key]).filter(([, value]) => value && value.trim() !== ''),
        ),
      ]).filter(([, value]) => Object.keys(value as object).length > 0),
    );
    return {
      flags: flagsDiffer ? flags : null,
      loginDefault: loginDefault !== code.loginDefault ? loginDefault : null,
      roleNames: Object.keys(names).length > 0 ? names : null,
    };
  };

  const anyOverride =
    profile.overridden.flags || profile.overridden.loginDefault || profile.overridden.roleNames;
  const flagLabel = (key: string) => t(`beheer.orgType.flags.${key.replace(/\./g, '_')}`, key);
  const onOff = (value: boolean) =>
    value ? t('beheer.orgType.page.on') : t('beheer.orgType.page.off');

  return (
    <>
      <Note tone="info">
        {t('beheer.orgType.page.appliesTo', {
          type: t(`beheer.orgType.types.${profile.orgType}`),
          count: profile.tenantCount,
        })}
      </Note>

      <Card>
        <CardHead
          title={t('beheer.orgType.page.flagsTitle')}
          actions={
            profile.overridden.flags ? (
              <Badge tone="info">{t('beheer.orgType.page.custom')}</Badge>
            ) : null
          }
        />
        <CardPad>
          <p className="a-muted">{t('beheer.orgType.page.flagsIntro')}</p>
          {profileFlags.map((key) => (
            <SwitchRow
              key={key}
              title={flagLabel(key)}
              text={t('beheer.orgType.page.codeDefault', { value: onOff(code.flags[key] ?? true) })}
              checked={flags[key] ?? true}
              onChange={(checked) => setFlags((all) => ({ ...all, [key]: checked }))}
              testId={`org-type-flag-${key}`}
            />
          ))}
        </CardPad>
      </Card>

      <Card>
        <CardHead
          title={t('beheer.orgType.page.loginTitle')}
          actions={
            profile.overridden.loginDefault ? (
              <Badge tone="info">{t('beheer.orgType.page.custom')}</Badge>
            ) : null
          }
        />
        <CardPad>
          <Field
            label={t('beheer.orgType.page.loginLabel')}
            hint={t('beheer.orgType.page.loginHint', {
              value: t(`beheer.orgType.page.login_${code.loginDefault}`),
            })}
          >
            {(props) => (
              <Select
                {...props}
                data-testid="org-type-login"
                value={loginDefault}
                onChange={(event) => setLoginDefault(event.target.value as LoginDefault)}
              >
                <option value="email">{t('beheer.orgType.page.login_email')}</option>
                <option value="pin">{t('beheer.orgType.page.login_pin')}</option>
              </Select>
            )}
          </Field>
        </CardPad>
      </Card>

      <Card>
        <CardHead
          title={t('beheer.orgType.page.rolesTitle')}
          actions={
            profile.overridden.roleNames ? (
              <Badge tone="info">{t('beheer.orgType.page.custom')}</Badge>
            ) : null
          }
        />
        <CardPad>
          <p className="a-muted">{t('beheer.orgType.page.rolesIntro')}</p>
        </CardPad>
        <Table label={t('beheer.orgType.page.rolesTitle')} testId="org-type-roles">
          <thead>
            <tr>
              <th scope="col">{t('beheer.orgType.page.roleKey')}</th>
              {supportedLanguages.map((language) => (
                <th key={language} scope="col">
                  {language.toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROLE_KEYS.map((key) => (
              <tr key={key}>
                <th scope="row">
                  <code>{key}</code>
                </th>
                {supportedLanguages.map((language) => (
                  <td key={language}>
                    <Input
                      aria-label={t('beheer.orgType.page.roleField', {
                        role: key,
                        language: language.toUpperCase(),
                      })}
                      data-testid={`org-type-role-${key}-${language}`}
                      maxLength={60}
                      placeholder={code.roleNames[key]?.[language] ?? ''}
                      value={roleNames[key][language] ?? ''}
                      onChange={(event) =>
                        setRoleNames((all) => ({
                          ...all,
                          [key]: { ...all[key], [language]: event.target.value },
                        }))
                      }
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      {save.isError ? (
        <Note tone="danger" role="alert">
          {t('errors.generic')}
        </Note>
      ) : null}

      <div className="a-actions">
        {anyOverride ? (
          <Btn
            variant="sec"
            disabled={save.isPending}
            onClick={() => save.mutate({ flags: null, loginDefault: null, roleNames: null })}
            testId="org-type-reset"
          >
            {t('beheer.orgType.page.reset')}
          </Btn>
        ) : null}
        <Btn
          disabled={save.isPending}
          onClick={() => save.mutate(request())}
          testId="org-type-save"
        >
          {t('beheer.orgType.page.save')}
        </Btn>
      </div>
    </>
  );
}
